// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useMemo, useState } from 'react'
import { formatUsTimestamp } from '../../api/format'
import { Activity, Loader2, AlertCircle, DollarSign, Database, ListChecks, Cloud } from 'lucide-react'
import * as api from '../../api/client'
import CostTrendsPanel from './CostTrendsPanel'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

/**
 * SaaS-admin Operations dashboard. App-level metrics Azure can't see: per-pharmacy AI token spend
 * (cost control), ingest queue health, and activity volume. The Azure live-metrics panel is a
 * labeled placeholder until a Cost Management / Azure Monitor read grant is wired — we don't
 * fabricate cost/latency numbers.
 */
export default function OpsDashboardTab() {
  const [data, setData] = useState<api.OpsDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void load() }, [])
  async function load() {
    setLoading(true); setError(null)
    try { setData(await api.getOpsDashboard(30)) }
    catch (e: any) { setError(e?.message || 'Failed to load operations data') }
    finally { setLoading(false) }
  }

  // D-UI-2: one set of header funnels per table. Each filters the whole array its table holds;
  // any sort / top-N cut happens after.
  const services = useMemo(
    () => (data?.azure_cost?.by_service ?? []).slice().sort((a, b) => b.cost - a.cost), [data])
  const resources = useMemo(
    () => (data?.azure_cost?.by_resource ?? []).filter(r => r.cost > 0), [data])
  const databases = useMemo<DbRow[]>(
    () => (data?.azure_sql_databases?.databases ?? [])
      .map(db => {
        const costRow = data?.azure_cost?.by_resource?.find(r => r.resource === db.database)
        return { ...db, cost: costRow?.cost ?? 0 }
      })
      .sort((a, b) => b.cost - a.cost), [data])
  const aiFx = useTableFunnels(data?.ai.by_pharmacy, AI_FUNNELS)
  const custFx = useTableFunnels(data?.per_customer_cost?.customers, CUSTOMER_FUNNELS)
  const actFx = useTableFunnels(data?.activity.top_actions, ACTION_FUNNELS)
  const svcFx = useTableFunnels(services, SERVICE_FUNNELS)
  const resFx = useTableFunnels(resources, RESOURCE_FUNNELS)
  const dbFx = useTableFunnels(databases, DB_FUNNELS)
  const perfFx = useTableFunnels(data?.azure_performance?.metrics, METRIC_FUNNELS)

  return (
    <div style={{ maxWidth: '100%' }}>
      <header style={{ marginBottom: 16 }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 4px' }}>
          <Activity size={18} /> Operations
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-mute, #6b7280)' }}>
          App-level cost, queue, and activity metrics. {data && <>Updated {formatUsTimestamp(data.generated_at)}.</>}
        </p>
      </header>

      {error && <div style={errBox}><AlertCircle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{error}</div>}

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-mute)' }}>
          <Loader2 size={18} className="spin" /> Loading…
        </div>
      ) : data ? (
        <>
          {/* Panel 1: AI cost */}
          <section style={card}>
            <h3 style={h3}><DollarSign size={15} /> AI usage — month to date</h3>
            <div style={statRow}>
              <Stat label="Total cost" value={`$${data.ai.month_to_date.cost.toFixed(2)}`} emphasis />
              <Stat label="Calls" value={data.ai.month_to_date.calls.toLocaleString()} />
              <Stat label="Input tokens" value={data.ai.month_to_date.input.toLocaleString()} />
              <Stat label="Output tokens" value={data.ai.month_to_date.output.toLocaleString()} />
            </div>
            {data.ai.by_pharmacy.length === 0 ? (
              <p style={muted}>No AI usage this month.</p>
            ) : (
              <>
              {aiFx.chips}
              <table {...aiFx.tableProps} style={table}>
                <thead><tr>
                  <th style={th}>Pharmacy {aiFx.funnel('name')}</th><th style={thR}>Calls {aiFx.funnel('calls')}</th><th style={thR}>Tokens {aiFx.funnel('tokens')}</th>
                  <th style={thR}>Cost {aiFx.funnel('cost')}</th><th style={thR}>Limit {aiFx.funnel('limit')}</th><th style={thR}>Used {aiFx.funnel('used')}</th>
                </tr></thead>
                <tbody>
                  {aiFx.rows.map((p, i) => (
                    <tr key={i}>
                      <td style={td}>{p.name}</td>
                      <td style={tdR}>{p.calls.toLocaleString()}</td>
                      <td style={tdR}>{p.total_tokens.toLocaleString()}</td>
                      <td style={tdR}>${p.cost_usd.toFixed(2)}</td>
                      <td style={tdR}>{p.monthly_token_limit != null ? p.monthly_token_limit.toLocaleString() : '—'}</td>
                      <td style={tdR}>
                        {p.pct_of_limit != null
                          ? <span style={{ color: p.pct_of_limit >= 90 ? '#b91c1c' : p.pct_of_limit >= 70 ? '#b45309' : '#15803d', fontWeight: 600 }}>{p.pct_of_limit}%</span>
                          : <span style={{ color: 'var(--color-text-mute)' }}>no cap</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            )}
          </section>

          {/* Cost per customer */}
          {data.per_customer_cost && data.per_customer_cost.customers.length > 0 && (
            <section style={card}>
              <h3 style={h3}><DollarSign size={15} /> Cost per customer — month to date</h3>
              {custFx.chips}
              <table {...custFx.tableProps} style={table}>
                <thead><tr>
                  <th style={th}>Customer {custFx.funnel('name')}</th>
                  <th style={thR}>Tenant DB {custFx.funnel('db')}</th>
                  <th style={thR}>AI {custFx.funnel('ai')}</th>
                  <th style={thR}>Total {custFx.funnel('total')}</th>
                </tr></thead>
                <tbody>
                  {custFx.rows.map((c, i) => (
                    <tr key={i}>
                      <td style={td}>{c.name}</td>
                      <td style={tdR}>${c.tenant_db_cost_usd.toFixed(2)}</td>
                      <td style={tdR}>${c.ai_cost_usd.toFixed(2)}</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>${c.total_cost_usd.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: 11, color: 'var(--color-text-mute)', margin: '6px 0 0' }}>
                {data.per_customer_cost.note}
              </p>
            </section>
          )}

          {/* Panel 2: ingest queue */}
          <section style={card}>
            <h3 style={h3}><Database size={15} /> Ingest queue health</h3>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <StatusGroup title="Parse status" counts={data.ingest_queue.parse_status} badKeys={['parse_error']} />
              <StatusGroup title="Mirror status" counts={data.ingest_queue.mirror_status} badKeys={['mirror_error']} />
            </div>
          </section>

          {/* Panel 3: activity */}
          <section style={card}>
            <h3 style={h3}><ListChecks size={15} /> Activity — last {data.window_days} days</h3>
            <Stat label="Audit events" value={data.activity.audit_events.toLocaleString()} emphasis />
            {data.activity.top_actions.length > 0 && (
              <>
              {actFx.chips}
              <table {...actFx.tableProps} style={{ ...table, marginTop: 10 }}>
                <thead><tr><th style={th}>Action {actFx.funnel('action')}</th><th style={thR}>Count {actFx.funnel('count')}</th></tr></thead>
                <tbody>
                  {actFx.rows.map((a, i) => (
                    <tr key={i}><td style={td}><code style={{ fontSize: 12 }}>{a.action}</code></td><td style={tdR}>{a.count.toLocaleString()}</td></tr>
                  ))}
                </tbody>
              </table>
              </>
            )}
          </section>

          {/* Panel 4: live Azure cost + performance */}
          <section style={card}>
            <h3 style={h3}><Cloud size={15} /> Azure cost &amp; performance</h3>

            {/* Cost */}
            {data.azure_cost?.available ? (
              <div style={{ marginBottom: 14 }}>
                <Stat label={`Cost month-to-date (${data.azure_cost.currency || 'USD'})`}
                      value={`$${(data.azure_cost.month_to_date_cost || 0).toFixed(2)}`} emphasis />
                {data.azure_cost.by_service && data.azure_cost.by_service.length > 0 && (
                  <>
                  {svcFx.chips}
                  <table {...svcFx.tableProps} style={{ ...table, marginTop: 10 }}>
                    <thead><tr><th style={th}>Service {svcFx.funnel('service')}</th><th style={thR}>Cost {svcFx.funnel('cost')}</th></tr></thead>
                    <tbody>
                      {svcFx.rows.map((s, i) => (
                        <tr key={i}><td style={td}>{s.service}</td><td style={tdR}>${s.cost.toFixed(2)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                  </>
                )}
                {data.azure_cost.by_resource && data.azure_cost.by_resource.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 600, margin: '12px 0 4px' }}>Top resources by cost</div>
                    {resFx.chips}
                    <table {...resFx.tableProps} style={table}>
                      <thead><tr><th style={th}>Resource {resFx.funnel('resource')}</th><th style={thR}>Cost {resFx.funnel('cost')}</th></tr></thead>
                      <tbody>
                        {resFx.rows.slice(0, 8).map((r, i) => (
                          <tr key={i}><td style={td} title={r.resource_id}><code style={{ fontSize: 12 }}>{r.resource}</code></td><td style={tdR}>${r.cost.toFixed(2)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            ) : (
              <div style={{ background: '#f1f5f9', border: '1px dashed #cbd5e1', borderRadius: 6, padding: 12, fontSize: 13, color: '#475569', marginBottom: 12 }}>
                Cost data unavailable{data.azure_cost?.reason ? ` — ${data.azure_cost.reason}` : ''}. If you just granted the role, allow a few minutes for it to propagate.
              </div>
            )}

            {/* Per-database cost + tier — the cost-control view */}
            {data.azure_sql_databases?.available && data.azure_sql_databases.databases && data.azure_sql_databases.databases.length > 0 ? (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, margin: '12px 0 4px' }}>SQL databases — tier &amp; cost</div>
                {dbFx.chips}
                <table {...dbFx.tableProps} style={table}>
                  <thead><tr><th style={th}>Database {dbFx.funnel('database')}</th><th style={th}>Tier {dbFx.funnel('tier')}</th><th style={thR}>Compute {dbFx.funnel('compute')}</th><th style={thR}>MTD cost {dbFx.funnel('cost')}</th></tr></thead>
                  <tbody>
                    {dbFx.rows
                      .map((db, i) => (
                        <tr key={i}>
                          <td style={td}><code style={{ fontSize: 12 }}>{db.database}</code></td>
                          <td style={td}>{db.tier}{db.capacity != null ? ` (${db.capacity})` : ''}</td>
                          <td style={tdR}>
                            <span style={{ color: db.compute === 'serverless' ? '#15803d' : '#b45309', fontWeight: 600 }}>{db.compute}</span>
                          </td>
                          <td style={tdR}>${db.cost.toFixed(2)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                <p style={{ fontSize: 11, color: 'var(--color-text-mute)', margin: '6px 0 0' }}>
                  Provisioned databases are billed 24/7 regardless of use. Serverless databases auto-pause when idle.
                </p>
              </>
            ) : data.azure_sql_tier?.reason ? (
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--color-text-mute)' }}>
                SQL tier unavailable — {data.azure_sql_tier.reason}
              </div>
            ) : null}

            {/* Performance */}
            {data.azure_performance?.available && data.azure_performance.metrics ? (
              <>
              {perfFx.chips}
              <table {...perfFx.tableProps} style={table}>
                <thead><tr><th style={th}>Metric (24h) {perfFx.funnel('metric')}</th><th style={thR}>Avg {perfFx.funnel('avg')}</th><th style={thR}>Peak {perfFx.funnel('peak')}</th><th style={thR}>Total {perfFx.funnel('total')}</th></tr></thead>
                <tbody>
                  {perfFx.rows.map((m, i) => (
                    <tr key={i}>
                      <td style={td}>{METRIC_LABELS[m.name] || m.name}{m.unit ? <span style={{ color: 'var(--color-text-mute)', fontSize: 11 }}> ({m.unit})</span> : null}</td>
                      <td style={tdR}>{m.avg.toFixed(1)}</td>
                      <td style={tdR}>{(m.peak ?? 0).toFixed(1)}</td>
                      <td style={tdR}>{m.sum.toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            ) : (
              <div style={{ background: '#f1f5f9', border: '1px dashed #cbd5e1', borderRadius: 6, padding: 12, fontSize: 13, color: '#475569' }}>
                Performance metrics unavailable{data.azure_performance?.reason ? ` — ${data.azure_performance.reason}` : ''}.
              </div>
            )}
          </section>

          <CostTrendsPanel />
        </>
      ) : null}
    </div>
  )
}

function Stat({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: emphasis ? 22 : 16, fontWeight: 700, color: 'var(--color-text)' }}>{value}</div>
    </div>
  )
}

function StatusGroup({ title, counts, badKeys }: { title: string; counts: Record<string, number>; badKeys: string[] }) {
  const keys = Object.keys(counts)
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{title}</div>
      {keys.length === 0 ? <div style={muted}>No events.</div> : keys.map(k => {
        const bad = badKeys.includes(k) && counts[k] > 0
        return (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 13, padding: '2px 0' }}>
            <span style={{ color: bad ? '#b91c1c' : 'var(--color-text)', fontWeight: bad ? 700 : 400 }}>{k}</span>
            <span style={{ fontWeight: 600, color: bad ? '#b91c1c' : 'var(--color-text)' }}>{counts[k].toLocaleString()}</span>
          </div>
        )
      })}
    </div>
  )
}

const METRIC_LABELS: Record<string, string> = {
  FunctionExecutionCount: 'Function executions',
  FunctionExecutionUnits: 'Execution units (GB-s proxy)',
  HttpResponseTime: 'HTTP response time',
  Requests: 'HTTP requests',
  Http5xx: 'HTTP 5xx errors',
}

// D-UI-2: what each column of each table shows, for its header funnel.
type AiRow = api.OpsDashboard['ai']['by_pharmacy'][number]
type CustomerRow = NonNullable<api.OpsDashboard['per_customer_cost']>['customers'][number]
type ActionRow = api.OpsDashboard['activity']['top_actions'][number]
type ServiceRow = NonNullable<NonNullable<api.OpsDashboard['azure_cost']>['by_service']>[number]
type ResourceRow = NonNullable<NonNullable<api.OpsDashboard['azure_cost']>['by_resource']>[number]
type DbRow = NonNullable<NonNullable<api.OpsDashboard['azure_sql_databases']>['databases']>[number] & { cost: number }
type MetricRow = NonNullable<NonNullable<api.OpsDashboard['azure_performance']>['metrics']>[number]

const AI_FUNNELS: FunnelColumn<AiRow>[] = [
  { key: 'name',   label: 'Pharmacy', value: p => p.name },
  { key: 'calls',  label: 'Calls',    value: p => p.calls },
  { key: 'tokens', label: 'Tokens',   value: p => p.total_tokens },
  { key: 'cost',   label: 'Cost',     value: p => Number(p.cost_usd.toFixed(2)) },
  { key: 'limit',  label: 'Limit',    value: p => p.monthly_token_limit },
  { key: 'used',   label: 'Used',     value: p => p.pct_of_limit != null ? `${p.pct_of_limit}%` : 'no cap' },
]
const CUSTOMER_FUNNELS: FunnelColumn<CustomerRow>[] = [
  { key: 'name',  label: 'Customer',  value: c => c.name },
  { key: 'db',    label: 'Tenant DB', value: c => Number(c.tenant_db_cost_usd.toFixed(2)) },
  { key: 'ai',    label: 'AI',        value: c => Number(c.ai_cost_usd.toFixed(2)) },
  { key: 'total', label: 'Total',     value: c => Number(c.total_cost_usd.toFixed(2)) },
]
const ACTION_FUNNELS: FunnelColumn<ActionRow>[] = [
  { key: 'action', label: 'Action', value: a => a.action },
  { key: 'count',  label: 'Count',  value: a => a.count },
]
const SERVICE_FUNNELS: FunnelColumn<ServiceRow>[] = [
  { key: 'service', label: 'Service', value: s => s.service },
  { key: 'cost',    label: 'Cost',    value: s => Number(s.cost.toFixed(2)) },
]
const RESOURCE_FUNNELS: FunnelColumn<ResourceRow>[] = [
  { key: 'resource', label: 'Resource', value: r => r.resource },
  { key: 'cost',     label: 'Cost',     value: r => Number(r.cost.toFixed(2)) },
]
const DB_FUNNELS: FunnelColumn<DbRow>[] = [
  { key: 'database', label: 'Database', value: d => d.database },
  { key: 'tier',     label: 'Tier',     value: d => `${d.tier ?? ''}${d.capacity != null ? ` (${d.capacity})` : ''}` },
  { key: 'compute',  label: 'Compute',  value: d => d.compute },
  { key: 'cost',     label: 'MTD cost', value: d => Number(d.cost.toFixed(2)) },
]
const METRIC_FUNNELS: FunnelColumn<MetricRow>[] = [
  { key: 'metric', label: 'Metric (24h)', value: m => METRIC_LABELS[m.name] || m.name },
  { key: 'avg',    label: 'Avg',          value: m => Number(m.avg.toFixed(1)) },
  { key: 'peak',   label: 'Peak',         value: m => Number((m.peak ?? 0).toFixed(1)) },
  { key: 'total',  label: 'Total',        value: m => Number(m.sum.toFixed(0)) },
]
const card: React.CSSProperties = { border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, padding: 16, marginBottom: 14, background: 'var(--color-surface, #fff)' }
const h3: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, margin: '0 0 12px' }
const statRow: React.CSSProperties = { display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 14 }
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--color-border, #e5e7eb)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-mute)' }
const thR: React.CSSProperties = { ...th, textAlign: 'right' }
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--color-border, #f3f4f6)' }
const tdR: React.CSSProperties = { ...td, textAlign: 'right' }
const muted: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-mute)', margin: '4px 0' }
const errBox: React.CSSProperties = { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: 10, fontSize: 13, color: '#991b1b', marginBottom: 12 }
